/**
 * The queue's pure rules: which cases belong in it, and what a row links to.
 *
 * Their own module because the lanes they serve import the API adapters, and
 * through them half the package graph — a test of a rule should not be able to
 * fail because an unrelated package is mid-rebuild. Same reason the rest of this
 * codebase extracts its decisions: `caseActs`, `decodeQuality`, `dustProjection`.
 */
import { toStoredChainId } from "@authe/fe-commons/chains";
import type { Investigation } from "@/api/investigationsSource";

/** How many rows a lane shows before deferring to its own page. */
export const LANE_SIZE = 5;

export interface QueueCase {
  id: string;
  title: string;
  status: Investigation["status"];
  watches: number;
  exhibits: number;
  /**
   * When the case last MOVED, epoch ms.
   *
   * The wire's `updated_at` / `last_activity_at` are seconds and the source
   * already scales them, so this is a pass-through — scaling again put every
   * case "in 56,674 years", which is what shipped for about ten minutes. It is
   * the max of the two for the same reason the Investigations list takes it: a
   * teammate touching the case and the case's own row moving are both activity.
   */
  updatedAtMs: number;
}

/**
 * Which cases belong in a queue, in the order the source already answered in.
 *
 * Archived cases are dropped — a queue is what is still open; they remain on the
 * Investigations page, which is what "See all" leads to. The order is NOT
 * recomputed: the source lists newest activity first, and a second ranking here
 * would let the queue and that page disagree about what is recent.
 */
export function queueCases(cases: readonly Investigation[]): QueueCase[] {
  return cases
    .filter((c) => c.status !== "archived")
    .slice(0, LANE_SIZE)
    .map((c) => ({
      id: c.id,
      title: c.title,
      status: c.status,
      watches: c.watches?.length ?? 0,
      exhibits: c.evidenceCount ?? 0,
      updatedAtMs: Math.max(c.lastActivityAt ?? 0, c.updatedAt ?? 0),
    }));
}

/**
 * A value `/transactions/$id` can resolve: a canonical id passes through, and a
 * bare hash is scoped to its chain. Mirrors `HeaderSearch`'s `transactionRouteId`
 * — the one place this convention was written down.
 */
export function transactionRef(hash: string, chainId: string | null): string {
  if (!hash) return "";
  if (hash.includes(":") || !hash.toLowerCase().startsWith("0x")) return hash;
  const chainToken = toStoredChainId(chainId) ?? chainId;
  return chainToken ? `${chainToken}:${hash}` : hash;
}
